import type { PixelSink } from '../crt/pixelFont';

// Big pixel font for the cartridge's top panel (TERMINAL.md §6.1): 6×10 with
// two-pixel strokes, so the damage number stays readable at
// the size a cartridge actually gets on screen (~65 render pixels wide). The
// CRT's 5×7 font is too light here and doubling it is too heavy, hence a set of
// its own. '#' is ink, '.' is empty.

export const BIG_W = 6;
export const BIG_H = 10;
/** Pixels from one glyph's left edge to the next. */
export const BIG_ADVANCE = BIG_W + 1;

const GLYPHS: Record<string, readonly string[]> = {
  '0': ['.####.', '######', '##..##', '##..##', '##..##', '##..##', '##..##', '##..##', '######', '.####.'],
  '1': ['..##..', '.###..', '####..', '..##..', '..##..', '..##..', '..##..', '..##..', '######', '######'],
  '2': ['.####.', '######', '##..##', '....##', '...##.', '..##..', '.##...', '##....', '######', '######'],
  '3': ['.####.', '######', '##..##', '....##', '..###.', '..###.', '....##', '##..##', '######', '.####.'],
  '4': ['...##.', '..###.', '.####.', '##.##.', '##.##.', '######', '######', '...##.', '...##.', '...##.'],
  '5': ['######', '######', '##....', '#####.', '######', '....##', '....##', '##..##', '######', '.####.'],
  '6': ['.####.', '######', '##..##', '##....', '#####.', '######', '##..##', '##..##', '######', '.####.'],
  '7': ['######', '######', '....##', '...##.', '...##.', '..##..', '..##..', '.##...', '.##...', '.##...'],
  '8': ['.####.', '######', '##..##', '##..##', '.####.', '.####.', '##..##', '##..##', '######', '.####.'],
  '9': ['.####.', '######', '##..##', '##..##', '######', '.#####', '....##', '##..##', '######', '.####.'],
  '+': ['......', '......', '..##..', '..##..', '######', '######', '..##..', '..##..', '......', '......'],
  '*': ['......', '..##..', '##..##', '.####.', '######', '.####.', '##..##', '..##..', '......', '......'],
  A: ['.####.', '######', '##..##', '##..##', '######', '######', '##..##', '##..##', '##..##', '##..##'],
  B: ['#####.', '######', '##..##', '##..##', '#####.', '#####.', '##..##', '##..##', '######', '#####.'],
  C: ['.####.', '######', '##..##', '##....', '##....', '##....', '##....', '##..##', '######', '.####.'],
  D: ['#####.', '######', '##..##', '##..##', '##..##', '##..##', '##..##', '##..##', '######', '#####.'],
  E: ['######', '######', '##....', '##....', '#####.', '#####.', '##....', '##....', '######', '######'],
  F: ['######', '######', '##....', '##....', '#####.', '#####.', '##....', '##....', '##....', '##....'],
  G: ['.####.', '######', '##..##', '##....', '##.###', '##.###', '##..##', '##..##', '######', '.####.'],
  H: ['##..##', '##..##', '##..##', '##..##', '######', '######', '##..##', '##..##', '##..##', '##..##'],
  I: ['######', '######', '..##..', '..##..', '..##..', '..##..', '..##..', '..##..', '######', '######'],
  J: ['..####', '..####', '....##', '....##', '....##', '....##', '##..##', '##..##', '######', '.####.'],
  K: ['##..##', '##.##.', '####..', '###...', '###...', '####..', '##.##.', '##..##', '##..##', '##..##'],
  L: ['##....', '##....', '##....', '##....', '##....', '##....', '##....', '##....', '######', '######'],
  // M is filled at the top, W at the bottom: at six pixels wide a real diagonal
  // would read as noise, and the two must never be mistaken for H.
  M: ['##..##', '######', '######', '##..##', '##..##', '##..##', '##..##', '##..##', '##..##', '##..##'],
  N: ['##..##', '###.##', '###.##', '######', '######', '##.###', '##.###', '##..##', '##..##', '##..##'],
  O: ['.####.', '######', '##..##', '##..##', '##..##', '##..##', '##..##', '##..##', '######', '.####.'],
  P: ['#####.', '######', '##..##', '##..##', '######', '#####.', '##....', '##....', '##....', '##....'],
  Q: ['.####.', '######', '##..##', '##..##', '##..##', '##..##', '##.###', '######', '.#####', '....##'],
  R: ['#####.', '######', '##..##', '##..##', '######', '#####.', '##.##.', '##..##', '##..##', '##..##'],
  S: ['.####.', '######', '##..##', '##....', '#####.', '.#####', '....##', '##..##', '######', '.####.'],
  T: ['######', '######', '..##..', '..##..', '..##..', '..##..', '..##..', '..##..', '..##..', '..##..'],
  U: ['##..##', '##..##', '##..##', '##..##', '##..##', '##..##', '##..##', '##..##', '######', '.####.'],
  V: ['##..##', '##..##', '##..##', '##..##', '##..##', '##..##', '.####.', '.####.', '..##..', '..##..'],
  W: ['##..##', '##..##', '##..##', '##..##', '##..##', '##..##', '##..##', '######', '######', '##..##'],
  X: ['##..##', '##..##', '.####.', '.####.', '..##..', '..##..', '.####.', '.####.', '##..##', '##..##'],
  Y: ['##..##', '##..##', '.####.', '.####.', '..##..', '..##..', '..##..', '..##..', '..##..', '..##..'],
  Z: ['######', '######', '....##', '...##.', '..##..', '..##..', '.##...', '##....', '######', '######'],
};

/** Width of `text` in pixels (no trailing spacing). */
export function measureBig(text: string): number {
  return text.length === 0 ? 0 : text.length * BIG_ADVANCE - 1;
}

/** True when every character of `text` has a glyph (a test checks the data). */
export function hasBigGlyphs(text: string): boolean {
  return [...text].every((ch) => GLYPHS[ch] !== undefined);
}

/**
 * Draws `text` at `x, y` in one flat colour (decision 2026-09-20: no outline —
 * the panel behind is dark enough to carry light glyphs on its own, and an
 * outline at this size only eats into the strokes).
 */
export function drawBig(sink: PixelSink, text: string, x: number, y: number, color: string): void {
  sink.fillStyle = color;
  [...text].forEach((ch, i) => {
    const glyph = GLYPHS[ch];
    if (!glyph) return;
    const left = x + i * BIG_ADVANCE;
    glyph.forEach((row, ry) => {
      let run = 0;
      for (let rx = 0; rx <= BIG_W; rx++) {
        if (row[rx] === '#') {
          run++;
          continue;
        }
        // Fill whole runs: one rect per run instead of one per pixel.
        if (run > 0) sink.fillRect(left + rx - run, y + ry, run, 1);
        run = 0;
      }
    });
  });
}
